import { UPLOAD_STATUS } from "../constants";
import { normalizeTranDateString } from "../utils/dateParse";
import { parseSalesExcelFile } from "./excelParserService";
import { validateAllRows } from "./validationService";

function normalizeParsedRowDates(rows) {
  for (const row of rows || []) {
    if (row.tranDate != null && row.tranDate !== "") {
      row.tranDate = normalizeTranDateString(row.tranDate);
    }
  }
}

/**
 * Weekly incremental pipeline: Excel → validate → duplicate detect.
 * Upload is a separate step (user confirms after preview).
 *
 * @param {File} file
 * @param {object} selectedWeekMeta from buildMonthWeekMeta (year, monthIndex, weekOfMonth, periodKey, …)
 * @param {(step: string, done: number, total: number, meta?: object) => void} onProgress
 */
export async function runWeeklyImportPipeline(file, selectedWeekMeta, onProgress) {
  if (!selectedWeekMeta?.periodKey || selectedWeekMeta?.weekOfMonth == null) {
    throw new Error("Select month and week before uploading.");
  }
  const ext = file.name.split(".").pop()?.toLowerCase();
  if (!["xlsx", "xls"].includes(ext)) {
    throw new Error("Only .xlsx and .xls files are supported.");
  }

  onProgress?.(UPLOAD_STATUS.PARSING, 0, 1);
  const { rows, headers } = await parseSalesExcelFile(file);
  normalizeParsedRowDates(rows);
  const weekMeta = selectedWeekMeta;

  onProgress?.(UPLOAD_STATUS.VALIDATING, 0, rows.length);
  const validation = await validateAllRows(rows, (done, total) => {
    onProgress?.(UPLOAD_STATUS.VALIDATING, done, total);
  });

  // Weekly upload replaces the selected month/week slice — skip slow per-doc existence checks.
  const existingIds = new Set();
  const firestoreDupes = 0;

  onProgress?.(UPLOAD_STATUS.READY, validation.validCount, validation.rows.length);

  return {
    rows,
    headers,
    validation,
    existingIds,
    weekMeta,
    fileMeta: {
      name: file.name,
      size: file.size,
      rows: rows.length,
      headers,
    },
    summary: {
      totalRecords: rows.length,
      validCount: validation.validCount,
      invalidCount: validation.invalidCount,
      duplicateInFile: validation.duplicateInFileCount,
      duplicateInFirestore: firestoreDupes,
      totalSalesAmount: validation.rows
        .filter((r) => r.valid)
        .reduce((s, r) => s + (Number(r.amount) || 0), 0),
    },
  };
}

/**
 * Monthly bulk pipeline: Excel → validate (full month; rows split into weeks on upload).
 *
 * @param {File} file
 * @param {object} selectedMonthMeta from buildMonthMeta
 * @param {(step: string, done: number, total: number, meta?: object) => void} onProgress
 */
export async function runMonthlyImportPipeline(file, selectedMonthMeta, onProgress) {
  if (!selectedMonthMeta?.periodKey || selectedMonthMeta?.fromDate == null) {
    throw new Error("Select month before uploading.");
  }
  const ext = file.name.split(".").pop()?.toLowerCase();
  if (!["xlsx", "xls"].includes(ext)) {
    throw new Error("Only .xlsx and .xls files are supported.");
  }

  onProgress?.(UPLOAD_STATUS.PARSING, 0, 1);
  const { rows, headers } = await parseSalesExcelFile(file);
  normalizeParsedRowDates(rows);
  const weekMeta = selectedMonthMeta;

  onProgress?.(UPLOAD_STATUS.VALIDATING, 0, rows.length);
  const validation = await validateAllRows(rows, (done, total) => {
    onProgress?.(UPLOAD_STATUS.VALIDATING, done, total);
  });

  const existingIds = new Set();
  const firestoreDupes = 0;

  onProgress?.(UPLOAD_STATUS.READY, validation.validCount, validation.rows.length);

  return {
    rows,
    headers,
    validation,
    existingIds,
    weekMeta,
    fileMeta: {
      name: file.name,
      size: file.size,
      rows: rows.length,
      headers,
    },
    summary: {
      totalRecords: rows.length,
      validCount: validation.validCount,
      invalidCount: validation.invalidCount,
      duplicateInFile: validation.duplicateInFileCount,
      duplicateInFirestore: firestoreDupes,
      totalSalesAmount: validation.rows
        .filter((r) => r.valid)
        .reduce((s, r) => s + (Number(r.amount) || 0), 0),
    },
  };
}

export const WORKFLOW_STEPS = [
  { id: "file", label: "Select period & file" },
  { id: UPLOAD_STATUS.PARSING, label: "Read Excel" },
  { id: UPLOAD_STATUS.VALIDATING, label: "Validate" },
  { id: "preview", label: "Preview" },
  { id: UPLOAD_STATUS.UPLOADING, label: "Upload" },
  { id: UPLOAD_STATUS.COMPLETE, label: "Summary" },
];
